"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import {
  COMMUNITY_ISSUE_LIMITS,
  COMMUNITY_ISSUE_STATUSES,
  COMMUNITY_ISSUE_STATUS_META,
  type CommunityIssueStatus,
} from "@/lib/community-issues";
import { setCommunityIssueStatus } from "@/lib/server-actions/community-issues/issues";

interface IssueStatusControlsProps {
  issueId: number;
  status: CommunityIssueStatus;
}

/**
 * 관리자 전용 상태 변경 패널. 노출은 `useAdminStatus()`, 최종 권한은 `setCommunityIssueStatus` 가 판단.
 * 복원 게시판 `PostAdminControls` 와 같은 amber 관리자 박스 스타일을 쓴다.
 */
export default function IssueStatusControls({ issueId, status }: IssueStatusControlsProps) {
  const router = useRouter();
  const { isAdmin } = useAdminStatus();
  const [nextStatus, setNextStatus] = useState<CommunityIssueStatus>(status);
  const [note, setNote] = useState("");
  const [isPending, startTransition] = useTransition();

  if (!isAdmin) return null;

  const unchanged = nextStatus === status && note.trim().length === 0;
  const closingRequiresReason = nextStatus === "closed" && status !== "closed";

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (unchanged) return;
    startTransition(async () => {
      const result = await setCommunityIssueStatus(issueId, nextStatus, note.trim() || null);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success(`상태를 "${COMMUNITY_ISSUE_STATUS_META[result.issue.status].label}"(으)로 기록했어요.`);
      setNote("");
      router.refresh();
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-4 rounded-md border border-amber-200 bg-amber-50/40 px-3 py-3 dark:border-amber-900/60 dark:bg-amber-950/20"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
          관리자
        </span>
        <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-neutral-300">
          상태
          <select
            value={nextStatus}
            onChange={(e) => setNextStatus(e.target.value as CommunityIssueStatus)}
            disabled={isPending}
            className="h-7 rounded-md border border-gray-300 bg-white px-2 text-xs disabled:opacity-60 dark:border-neutral-600 dark:bg-neutral-800"
          >
            {COMMUNITY_ISSUE_STATUSES.map((key) => (
              <option key={key} value={key}>
                {COMMUNITY_ISSUE_STATUS_META[key].label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={isPending || unchanged}
          className="inline-flex h-7 items-center gap-1 rounded-md bg-amber-500 px-3 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
        >
          {isPending && <Loader2 className="size-3 animate-spin" aria-hidden />}
          기록
        </button>
      </div>
      <textarea
        value={note}
        maxLength={COMMUNITY_ISSUE_LIMITS.note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        required={closingRequiresReason}
        placeholder={
          closingRequiresReason
            ? "닫힘 사유 (필수) — 예: 중복 문의로 기존 답변을 안내했습니다"
            : "타임라인에 남길 메모 (선택) — 예: v2.3에서 수정 반영 예정"
        }
        className="mt-2 w-full resize-y rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-[#333] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
      />
    </form>
  );
}
